import { ColorSwatches, Input, Popover, type PaletteColor } from '@tomo/ui';
import { Search, X } from 'lucide-react';
import { useMemo, useState, type ReactElement } from 'react';
import { ICON_GROUPS, ICONS, iconToken } from '../../lib/icons.tsx';
import './icon-picker.css';

export interface IconPickerProps {
  /** Toggle element; or use `anchor` + `open` to open it programmatically (e.g. from a menu). */
  trigger?: ReactElement;
  anchor?: Element | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  value: string | null | undefined;
  onChange: (token: string | null) => void;
  color?: PaletteColor | null;
  onColorChange?: (color: PaletteColor) => void;
  allowClear?: boolean;
}

/** Pick a built-in icon (and optionally a color). */
export function IconPicker({ trigger, anchor, open, onOpenChange, value, onChange, color, onColorChange, allowClear }: IconPickerProps) {
  const [query, setQuery] = useState('');
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ICON_GROUPS;
    return [{ label: 'Results', icons: Object.keys(ICONS).filter((k) => k.includes(q)) }];
  }, [query]);
  // Documents from before the switch to icons can hold an emoji; highlight the icon it maps to.
  const selected = iconToken(value);

  return (
    <Popover trigger={trigger} anchor={anchor} open={open} onOpenChange={onOpenChange} width={316} padding="sm" initialFocus={-1} placement={anchor ? 'right-start' : 'bottom-start'}>
      {({ close }) => (
        <div className="icon-picker">
          {allowClear && value && (
            <div className="icon-picker__top">
              <button
                type="button"
                className="icon-picker__clear"
                onClick={() => {
                  onChange(null);
                  close();
                }}
              >
                <X size={13} /> Remove
              </button>
            </div>
          )}
          {onColorChange && (
            <ColorSwatches value={color ?? null} onChange={onColorChange} size={20} className="icon-picker__colors" />
          )}
          <Input size="sm" leading={<Search />} placeholder="Search icons" value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
          <div className="icon-picker__scroll" data-color={color ?? undefined}>
            {groups.map((g) => (
              <div key={g.label}>
                <div className="icon-picker__group">{g.label}</div>
                <div className="icon-picker__grid">
                  {g.icons.map((name) => {
                    const C = ICONS[name]!;
                    const token = `i:${name}`;
                    return (
                      <button
                        key={name}
                        type="button"
                        title={name}
                        className="icon-picker__cell"
                        aria-pressed={selected === token}
                        onClick={() => {
                          onChange(token);
                          close();
                        }}
                      >
                        <C size={17} />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {!groups[0]?.icons.length && <div className="icon-picker__empty">No icons match “{query}”</div>}
          </div>
        </div>
      )}
    </Popover>
  );
}
